/**
 * Hash-based color assignment for product tag pills.
 *
 * Same product name always renders in the same color, across projects
 * and across page loads. Backed by a small fixed palette — picks one
 * via a simple deterministic hash of the product name.
 *
 * Each palette entry is a triple of Tailwind utility classes designed
 * to read as a quiet pill at small sizes: a soft background, a
 * higher-contrast text color, and a matching border. All entries pass
 * AA contrast at the typical 10-11px pill size.
 */

const PALETTE = [
  { bg: "bg-sky-100 dark:bg-sky-500/15",     text: "text-sky-700 dark:text-sky-300",     border: "border-sky-200 dark:border-sky-500/30" },
  { bg: "bg-emerald-100 dark:bg-emerald-500/15", text: "text-emerald-700 dark:text-emerald-300", border: "border-emerald-200 dark:border-emerald-500/30" },
  { bg: "bg-amber-100 dark:bg-amber-500/15",   text: "text-amber-700 dark:text-amber-300",   border: "border-amber-200 dark:border-amber-500/30" },
  { bg: "bg-rose-100 dark:bg-rose-500/15",    text: "text-rose-700 dark:text-rose-300",    border: "border-rose-200 dark:border-rose-500/30" },
  { bg: "bg-violet-100 dark:bg-violet-500/15",  text: "text-violet-700 dark:text-violet-300",  border: "border-violet-200 dark:border-violet-500/30" },
  { bg: "bg-teal-100 dark:bg-teal-500/15",    text: "text-teal-700 dark:text-teal-300",    border: "border-teal-200 dark:border-teal-500/30" },
  { bg: "bg-pink-100 dark:bg-pink-500/15",    text: "text-pink-700 dark:text-pink-300",    border: "border-pink-200 dark:border-pink-500/30" },
  { bg: "bg-orange-100 dark:bg-orange-500/15",  text: "text-orange-700 dark:text-orange-300",  border: "border-orange-200 dark:border-orange-500/30" },
  { bg: "bg-brand-navy-100 dark:bg-brand-navy-500/15",  text: "text-brand-navy-700 dark:text-brand-navy-300",  border: "border-brand-navy-200 dark:border-brand-navy-500/30" },
  { bg: "bg-cyan-100 dark:bg-cyan-500/15",    text: "text-cyan-700 dark:text-cyan-300",    border: "border-cyan-200 dark:border-cyan-500/30" },
] as const;

export interface ProductColor {
  bg: string;
  text: string;
  border: string;
}

/** Deterministic color for a product name. Trivial hash, stable across
 *  page loads. Empty strings get the first palette entry. */
export function productColor(name: string): ProductColor {
  if (!name) return PALETTE[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) >>> 0;
  }
  return PALETTE[h % PALETTE.length];
}
